import Phaser from 'phaser';
import type { Portal } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';

/**
 * Bulle de présence au-dessus des portails (composant BullePresence du
 * design system de Charles) : qui est sur la carte de l'autre côté, pour
 * savoir où se retrouver. Jusqu'à 3 têtes, « … » au-delà, puis le nombre
 * total en chiffres de pixels. Aucune bulle si la carte cible est vide.
 *
 * Données : présence Redis de tout le campus (`allZonePresence` du store,
 * GET /world/presence/all toutes les 20 s), qui donne l'empreinte de la
 * planche Pixel Citizen de chacun. Affichage seulement, rien via Colyseus.
 */

const CREME = '#fbf5e2'; // ui-creme
const NUIT = '#2b2530';  // ui-nuit
const HEADS_MAX = 3;     // tetes-max
const HEAD_W = 26;       // tete-bulle-largeur
const HEAD_H = 22;       // tete-bulle-hauteur
const HEAD_STEP = 19;    // tete-bulle-pas
const PAD = 2;           // marge intérieure, entre le bord et le contenu
const GAP = 3;           // entre les têtes, les points et le nombre
const TIP_ROWS = 4;      // pointe vers le portail
const TIP_GAP = 3;       // pixels vides entre le bout de la pointe et le portail
/** Devant le décor et les avatars, sous le voile hors zone (850). */
export const PORTAL_PRESENCE_DEPTH = 840;

const PC_FRAME = 48;

/** Chiffres de 5 × 7 pixels, une chaîne par rangée. */
const DIGITS: Record<string, string[]> = {
  '0': ['01110', '10001', '10011', '10101', '11001', '10001', '01110'],
  '1': ['00100', '01100', '00100', '00100', '00100', '00100', '01110'],
  '2': ['01110', '10001', '00001', '00010', '00100', '01000', '11111'],
  '3': ['11110', '00001', '00001', '01110', '00001', '00001', '11110'],
  '4': ['00010', '00110', '01010', '10010', '11111', '00010', '00010'],
  '5': ['11111', '10000', '11110', '00001', '00001', '10001', '01110'],
  '6': ['00110', '01000', '10000', '11110', '10001', '10001', '01110'],
  '7': ['11111', '00001', '00010', '00100', '01000', '01000', '01000'],
  '8': ['01110', '10001', '10001', '01110', '10001', '10001', '01110'],
  '9': ['01110', '10001', '10001', '01111', '00001', '00010', '01100'],
};

export interface PresencePerson { userId: string; sheet?: string }

// ── Têtes ───────────────────────────────────────────────────────────────────

/** Tête découpée dans l'image de repos de face (case 0 de la planche), par empreinte. */
const headCache = new Map<string, Promise<HTMLCanvasElement | null>>();

function loadHead(sheet: string): Promise<HTMLCanvasElement | null> {
  let p = headCache.get(sheet);
  if (!p) {
    p = cutHead(sheet).catch(() => null);
    headCache.set(sheet, p);
    // Échec (réseau) : on réessaiera au prochain rafraîchissement
    void p.then((head) => { if (!head) headCache.delete(sheet); });
  }
  return p;
}

async function cutHead(sheet: string): Promise<HTMLCanvasElement | null> {
  const res = await fetch(`${API_BASE}/assets/shared/avatars/pc/${sheet}.png`);
  if (!res.ok) return null;
  const bitmap = await createImageBitmap(await res.blob());
  const frame = document.createElement('canvas');
  frame.width = PC_FRAME;
  frame.height = PC_FRAME;
  const fctx = frame.getContext('2d', { willReadFrequently: true });
  if (!fctx) return null;
  fctx.drawImage(bitmap, 0, 0, PC_FRAME, PC_FRAME, 0, 0, PC_FRAME, PC_FRAME);
  bitmap.close();
  // Haut du crâne = première rangée avec un pixel opaque
  const data = fctx.getImageData(0, 0, PC_FRAME, PC_FRAME).data;
  let top = 0;
  find: for (let y = 0; y < PC_FRAME; y++) {
    for (let x = 0; x < PC_FRAME; x++) {
      if ((data[(y * PC_FRAME + x) * 4 + 3] ?? 0) > 0) { top = y; break find; }
    }
  }
  const head = document.createElement('canvas');
  head.width = HEAD_W;
  head.height = HEAD_H;
  head.getContext('2d')?.drawImage(frame, (PC_FRAME - HEAD_W) / 2, top, HEAD_W, HEAD_H, 0, 0, HEAD_W, HEAD_H);
  return head;
}

// ── Dessin de la bulle ──────────────────────────────────────────────────────

/** Bulle complète (pointe comprise). `tipX` = colonne du bout de la pointe. */
function drawBubble(heads: HTMLCanvasElement[], total: number): { canvas: HTMLCanvasElement; tipX: number } {
  const digits = String(total).split('');
  const numW = digits.length * 6 - 1;
  const dotsW = 8; // trois points de 2 × 2, espacés d'un pixel
  const showDots = total > heads.length && heads.length > 0;
  const headsW = heads.length ? HEAD_W + (heads.length - 1) * HEAD_STEP : 0;
  const contentH = heads.length ? HEAD_H : 7;
  const contentW = headsW
    + (showDots ? (headsW ? GAP : 0) + dotsW : 0)
    + (headsW || showDots ? GAP : 0) + numW;

  const W = contentW + 2 * PAD + 2;
  const H = contentH + 2 * PAD + 2;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H + TIP_ROWS;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  // Cadre : fond crème, bord nuit d'un pixel, coins coupés d'un pixel
  ctx.fillStyle = CREME;
  ctx.fillRect(1, 1, W - 2, H - 2);
  ctx.fillStyle = NUIT;
  ctx.fillRect(1, 0, W - 2, 1);
  ctx.fillRect(1, H - 1, W - 2, 1);
  ctx.fillRect(0, 1, 1, H - 2);
  ctx.fillRect(W - 1, 1, 1, H - 2);

  // Pointe de 4 rangs, centrée, ouverte dans le bord du bas
  const cx = Math.floor(W / 2);
  ctx.fillStyle = CREME;
  ctx.fillRect(cx - 2, H - 1, 5, 1);
  for (let r = 0; r < TIP_ROWS; r++) {
    const h = TIP_ROWS - 1 - r;
    ctx.fillStyle = CREME;
    if (h > 0) ctx.fillRect(cx - h + 1, H + r, 2 * h - 1, 1);
    ctx.fillStyle = NUIT;
    ctx.fillRect(cx - h, H + r, 1, 1);
    ctx.fillRect(cx + h, H + r, 1, 1);
  }

  // Têtes : la première à gauche, devant les autres
  let x = 1 + PAD;
  const top = 1 + PAD;
  heads.forEach((_, j) => {
    const i = heads.length - 1 - j;
    ctx.drawImage(heads[i]!, x + i * HEAD_STEP, top);
  });
  x += headsW;

  // Chiffres et points posés sur la même ligne, centrés en hauteur
  const glyphTop = top + Math.floor((contentH - 7) / 2);
  ctx.fillStyle = NUIT;
  if (showDots) {
    x += GAP;
    for (let d = 0; d < 3; d++) ctx.fillRect(x + d * 3, glyphTop + 5, 2, 2);
    x += dotsW;
  }
  if (headsW || showDots) x += GAP;
  for (const digit of digits) {
    const rows = DIGITS[digit] ?? [];
    for (let ry = 0; ry < 7; ry++) {
      for (let rx = 0; rx < 5; rx++) {
        if (rows[ry]?.[rx] === '1') ctx.fillRect(x + rx, glyphTop + ry, 1, 1);
      }
    }
    x += 6;
  }
  return { canvas, tipX: cx };
}

// ── Système ─────────────────────────────────────────────────────────────────

interface Bubble {
  image: Phaser.GameObjects.Image | null;
  textureKey: string | null;
  /** Contenu affiché (têtes + nombre) : rien n'est redessiné s'il ne change pas. */
  signature: string;
  generation: number;
}

let textureCounter = 0;

export class PortalPresence {
  private bubbles = new Map<string, Bubble>();
  private destroyed = false;

  constructor(private readonly scene: Phaser.Scene) {}

  /**
   * Recalcule les bulles. `presence` : personnes par carte (slug), le joueur
   * local déjà retiré ; `currentSlug` : la carte affichée (un portail vers
   * elle-même n'a pas de bulle).
   */
  update(portals: Portal[], presence: Map<string, PresencePerson[]>, currentSlug: string): void {
    if (this.destroyed) return;
    const seen = new Set<string>();
    for (const portal of portals) {
      const slug = portal.targetZoneId.slice(portal.targetZoneId.indexOf(':') + 1);
      const people = slug === currentSlug ? [] : (presence.get(slug) ?? []);
      if (people.length === 0) continue;
      seen.add(portal.id);
      this.render(portal, people);
    }
    for (const id of [...this.bubbles.keys()]) {
      if (!seen.has(id)) this.remove(id);
    }
  }

  private render(portal: Portal, people: PresencePerson[]): void {
    const sheets = people.filter((p) => p.sheet).slice(0, HEADS_MAX).map((p) => p.sheet!);
    const { x, y, width } = portal.triggerBounds;
    const anchorX = Math.round(x + width / 2);
    const anchorY = Math.round(y) - TIP_GAP;
    const signature = `${sheets.join(',')}|${people.length}`;

    let bubble = this.bubbles.get(portal.id);
    if (!bubble) {
      bubble = { image: null, textureKey: null, signature: '', generation: 0 };
      this.bubbles.set(portal.id, bubble);
    }
    if (bubble.image) bubble.image.setPosition(anchorX, anchorY);
    if (bubble.signature === signature) return;
    bubble.signature = signature;
    const generation = ++bubble.generation;
    const b = bubble;

    void Promise.all(sheets.map(loadHead)).then((loaded) => {
      // Périmé : bulle retirée, contenu changé entre-temps, ou scène fermée
      if (this.destroyed || this.bubbles.get(portal.id) !== b || b.generation !== generation) return;
      const heads = loaded.filter((h): h is HTMLCanvasElement => h !== null);
      const { canvas, tipX } = drawBubble(heads, people.length);
      const key = `portal-presence-${++textureCounter}`;
      const texture = this.scene.textures.addCanvas(key, canvas);
      texture?.setFilter(Phaser.Textures.FilterMode.NEAREST);
      if (!b.image) {
        b.image = this.scene.add.image(anchorX, anchorY, key).setDepth(PORTAL_PRESENCE_DEPTH);
      } else {
        b.image.setTexture(key);
      }
      b.image.setOrigin((tipX + 0.5) / canvas.width, 1);
      if (b.textureKey) this.scene.textures.remove(b.textureKey);
      b.textureKey = key;
    });
  }

  private remove(id: string): void {
    const bubble = this.bubbles.get(id);
    if (!bubble) return;
    bubble.image?.destroy();
    if (bubble.textureKey) this.scene.textures.remove(bubble.textureKey);
    this.bubbles.delete(id);
  }

  destroy(): void {
    for (const id of [...this.bubbles.keys()]) this.remove(id);
    this.destroyed = true;
  }
}
